import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { PromptValue } from '@azure/msal-common';
import { emailFirstSignIn, loginRequest } from '@app/auth-config';
import { Observable, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthenticationService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private sessionExpiredShown = false;

  constructor(
    private authService: AuthenticationService,
    private msalBroadcastService: MsalBroadcastService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // MSAL is the authority on whether we are signed in; our own stored user is
    // just a cache of it. The two can disagree — a loginUser that failed to write
    // (localStorage quota, swallowed by handleAzureLogin's catch) or no longer
    // parses leaves MSAL holding a live account with nothing stored on our side.
    //
    // Keying the early return on the cache alone made that disagreement fatal:
    // MsalGuard sees the account and lets routing through, but every call then
    // went out with NO Authorization header — a bare 401 that never reached the
    // retry path below, so no refresh, no Session Expired modal, just a cancelled
    // navigation and a blank page recoverable only by clearing site data.
    //
    // Deferring to MSAL repairs it instead: acquireTokenSilent succeeds off the
    // live account, and the ACQUIRE_TOKEN_SUCCESS it emits runs handleAzureLogin,
    // which rebuilds currentUser. That is the app's ONLY acquireTokenSilent call,
    // so gating it behind the cache is what made the state unrecoverable. Skip
    // only when neither source has anything.
    if (!this.authService.currentUser?.token && !this.authService.isLoggedIn()) {
      return next.handle(req);
    }

    // Hold requests until MSAL is idle (same gate Microsoft's own MsalInterceptor
    // uses). On the page load right after the B2C redirect, currentUser seeds
    // synchronously from localStorage (the PREVIOUS session's user), so boot-time
    // calls (terminology etc.) fire while MSAL is still exchanging the auth code —
    // at that moment there is no MSAL account, getApiToken() falls back to the
    // stale stored token, the API 401s, the force-refresh also finds no account,
    // and the user gets the "Session Expired" modal seconds after a successful
    // login. inProgress$ stays in Startup/HandleRedirect until the exchange
    // completes, so this gate queues those calls for the second or two it takes
    // and they then run with a fresh token.
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      // Proactive: ask MSAL for the bearer on every request. Cached token is
      // returned instantly; near expiry MSAL silently renews it, so active
      // users no longer ride tokens to expiry and bounce off a 401/re-login.
      // getApiToken() falls back to the stored token on MSAL failure, so the
      // 401 path below remains the only hard session gate.
      switchMap(() => from(this.authService.getApiToken())),
      switchMap((token) => next.handle(this.addAuthHeader(req, token))),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          // The API ended this session (sign-out elsewhere, password reset - pen test EVI-003-001/005).
          // Don't try to renew: a renewal can quietly ride this browser's own B2C cookie straight
          // back in, which is exactly what ending the session is meant to stop. Ask for the password.
          // Organisation sign-in (SSO) per licence: this way of signing in is not allowed for the
          // user's licence. A renewal would bring back the same refused token, so explain and send
          // them to the sign-in page to choose the other way.
          const refusal = this.signInRefusal(err);
          if (refusal) {
            return this.handleSignInRefused(refusal);
          }
          if (this.isSessionEnded(err)) {
            return this.handleExpired(true);
          }
          return this.refreshAndRetry(req, next);
        }
        return throwError(() => err);
      })
    );
  }

  // The API's answer for a revoked token (FoyleSoftAuthorizationAttribute), as opposed to an
  // ordinary expired one.
  private isSessionEnded(err: HttpErrorResponse): boolean {
    const message = typeof err.error === 'string' ? err.error : err.error?.message;
    return typeof message === 'string' && message.startsWith('Session ended');
  }

  // The API's answer when the sign-in method is not allowed for the user's licence
  // (FoyleSoftAuthorizationAttribute): "Sign-in refused: <reason>". Returns the reason, else null.
  private signInRefusal(err: HttpErrorResponse): string | null {
    const message = typeof err.error === 'string' ? err.error : err.error?.message;
    const prefix = 'Sign-in refused: ';
    return typeof message === 'string' && message.startsWith(prefix) ? message.substring(prefix.length) : null;
  }

  private handleSignInRefused(reason: string): Observable<never> {
    if (this.authService.isLoggingOut) {
      return throwError(() => new Error('Logging out'));
    }
    if (!this.sessionExpiredShown) {
      this.sessionExpiredShown = true;
      Swal.fire({
        title: "You can't sign in this way",
        text: reason,
        icon: 'warning',
        confirmButtonText: 'Back to sign-in',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: { confirmButton: 'btn btn-success' },
        buttonsStyling: false
      }).then(() => {
        this.sessionExpiredShown = false;
        // Back to the start so the user can choose the other way to sign in, rather than riding this
        // browser's B2C session straight back to the refused token: Evide's email page when email-first
        // sign-in is on, otherwise a fresh B2C sign-in page (prompt=login).
        this.authService.restartSignIn(true);
      });
    }
    return throwError(() => new Error('Sign-in refused'));
  }

  private addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ headers: req.headers.set('Authorization', 'Bearer ' + token) });
  }

  private refreshAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.authService.refreshToken()).pipe(
      switchMap((token) => next.handle(this.addAuthHeader(req, token))),
      catchError(() => this.handleExpired())
    );
  }

  private handleExpired(requirePassword = false): Observable<never> {
    // Signing out revokes this user's tokens first, so a request still in flight in this tab
    // comes back 401 a moment before the B2C redirect - don't greet a Log out click with
    // "Session Expired".
    if (this.authService.isLoggingOut) {
      return throwError(() => new Error('Logging out'));
    }
    if (!this.sessionExpiredShown) {
      this.sessionExpiredShown = true;
      Swal.fire({
        title: 'Session Expired',
        text: 'Your session has expired. Please log in again to continue.',
        icon: 'warning',
        confirmButtonText: 'Log In',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: { confirmButton: 'btn btn-success' },
        buttonsStyling: false
      }).then(() => {
        this.sessionExpiredShown = false;
        // Re-authenticate in place instead of logout(): logoutRedirect destroys
        // the B2C SSO cookie, which forced a full credentials + MFA round trip
        // every time this modal fired. login() records the current URL and rides
        // the SSO cookie — a still-valid B2C session signs straight back in and
        // returns the user to the page they were on; only a genuinely dead B2C
        // session shows the credential prompt (and then MFA is legitimately due).
        //
        // ...but always from a cleared cache (restartSignIn): login() alone went to B2C for the SAME
        // person and left the revoked sign-in in this browser, so backing out of B2C raised this box again
        // on every visit until a sign-in was completed (Niall, dev, 2026-10-29 - testing guide 5.4). With
        // email-first on, that also means Evide's email page with "Not you?"; without it, B2C as before.
        this.authService.restartSignIn(requirePassword);
      });
    }
    return throwError(() => new Error('Session expired'));
  }
}
